import { useRef, useState } from "react";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { COLOR_CONSTANTS } from "../../../../constants/styles";
import { SidebarItemButton } from "../SidebarItemButton";
import type { ApplicationSideBarItem } from "../../ApplicationSideBar";

export type SidebarOverflowMenuProps = {
  items: ApplicationSideBarItem[];
  activeItemId: string | null;
  loadingLocked: boolean;
  lockExemptItemIds: string[];
  onSelect: (item: ApplicationSideBarItem) => void;
};

function MoreIcon() {
  const tone = COLOR_CONSTANTS.action.active;
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx={5} cy={12} r={1.8} fill={tone} />
      <circle cx={12} cy={12} r={1.8} fill={tone} />
      <circle cx={19} cy={12} r={1.8} fill={tone} />
    </svg>
  );
}

export function SidebarOverflowMenu({
  items,
  activeItemId,
  loadingLocked,
  lockExemptItemIds,
  onSelect,
}: SidebarOverflowMenuProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const containsActive = items.some((item) => item.id === activeItemId);

  return (
    <>
      <Box ref={anchorRef} sx={anchorBox}>
        <SidebarItemButton
          icon={{ component: <MoreIcon />, name: "MORE" }}
          mode={containsActive ? "on" : "off"}
          onClick={() => {
            setAnchorEl(anchorRef.current);
            setOpen(true);
          }}
        />
      </Box>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: menuPaper } }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.id}
            selected={item.id === activeItemId}
            disabled={loadingLocked && !lockExemptItemIds.includes(item.id)}
            sx={menuItem}
            onClick={() => {
              setOpen(false);
              onSelect(item);
            }}
          >
            <ListItemIcon sx={menuItemIcon}>{item.icon.component}</ListItemIcon>
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{ sx: menuItemText }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const anchorBox = {
  width: "100%",
  display: "flex",
  justifyContent: "center",
} as const;

const menuPaper = {
  marginLeft: "8px",
  borderRadius: "8px",
  minWidth: "160px",
} as const;

const menuItem = {
  gap: "4px",
  padding: "8px 14px",
} as const;

const menuItemIcon = {
  minWidth: "28px",
} as const;

const menuItemText = {
  fontSize: "13px",
} as const;
